
import type { Result } from '@empathybroker/tsclient-play/dist/models/search/result'
import type {
  ApiError,
  ConflictApiError,
  NextQueries,
  ResultService,
  ResultServiceGetParams,
} from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type {
  NextQueriesMessages,
  RegularFilter,
  RegularFilterSelectedValues,
  ResolveConflictOption,
} from '../../models'
import type { DomainServiceProvider } from '../../providers/interfaces'
import type { NextQueriesDomainService } from '../../services/interfaces'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import ConflictErrorModalWithOptions from '../../components/crud/conflict-error-modal/conflict-error-modal-with-options.component.vue'
import NextQueriesConfig from '../../components/curation-tools/next-queries-config.component.vue'
import { UiAlert, UiConfirm, UiProgressLinear } from '../../components/keen-ui'
import ResultsGrid from '../../components/results-grid.component.vue'
import Searchbox from '../../components/searchbox.component.vue'
import ToolingFormLayout from '../../components/tooling/tooling-form-layout.component.vue'
import TransitionSize from '../../components/transition-size.component.vue'
import {
  useApiError,
  useCurationToolCriteria,
  useInstance,
  useToolingForm,
  useUpdateItemWithContext,
} from '../../composables'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { DeviceEnum, FeatureIdEnum, PositionEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { FiltersUtils } from '../../utils/filters.utils'

export default defineComponent({
  name: 'NextQueriesForm',
  components: {
    ConflictErrorModalWithOptions,
    NextQueriesConfig,
    ResultsGrid,
    Searchbox,
    ToolingFormLayout,
    TransitionSize,
    UiAlert,
    UiConfirm,
    UiProgressLinear,
  },
  emits: ['resetScroll', 'updateLoading', 'showError'],
  setup(props, ctx: SetupContext) {
    const { $epb, $route } = getCurrentInstance().proxy

    const messages: NextQueriesMessages = $epb.messages.tools.nextQueries

    const updateMode = $route.name === ROUTER_CONSTS.routes[FeatureIdEnum.NEXT_QUERIES].update
    const { updateItemWithContext } = useUpdateItemWithContext<NextQueries>()
    const toolingForm = useToolingForm<NextQueries>(props, ctx, {
      messages,
      updateMode,
      pivotsExtraConfig: {
        default: {
          mandatory: false,
          multiselect: true,
        },
        lang: {
          mandatory: true,
          multiselect: false,
        },
      },
    })

    const {
      conflictApiError,
      updatePivots,
      setCriteria,
      itemQuery,
      normalizedQuery,
      ...curationToolCriteria
    } = useCurationToolCriteria({
      featureId: toolingForm.featureId,
      item: toolingForm.item,
      pivotsConfig: toolingForm.pivotsConfig,
    })

    const query = computed(() => {
      return itemQuery.value
    })

    const { getErrorMessage, isConflictError } = useApiError()

    const resultService = CONTAINER.get<ResultService>(TYPES.ResultService)

    const domainService = CONTAINER.get<DomainServiceProvider>(TYPES.DomainServiceProvider)(
      toolingForm.featureId.value,
    ) as NextQueriesDomainService

    const baseClass = 'epb-next-queries-form'

    const searchbox = ref()

    const confirmCriteriaChangeModal = ref()

    const resolveConflictModal = ref()

    const changedCriteria = ref<NextQueries['criteria'] | undefined>(undefined)

    const searchData = ref<{ results: Array<Result>; total: number; loading: boolean }>({
      results: [],
      total: 0,
      loading: false,
    })

    const isValid = computed<boolean>(() =>
      domainService.isValid(toolingForm.item.value, {
        pivotsConfig: toolingForm.pivotsConfig.value,
      }),
    )

    const pivotsAreValid = computed<boolean>(() =>
      domainService.arePivotsValid(
        toolingForm.item.value.criteria.pivots,
        toolingForm.pivotsConfig.value,
      ),
    )

    const resultsPerRow = computed<number>(() => {
      if ($epb.messages.device <= DeviceEnum.MOBILE) {
        return 1
      }
      if ($epb.messages.device <= DeviceEnum.TABLET) {
        return 2
      }
      return 4
    })

    async function confirmAccept(updatePrevious?: NextQueries): Promise<void> {
      const queryToSave = query.value
      toolingForm.item.value.criteria.queries = [queryToSave]
      await toolingForm.onAccept({ errorHandler }, updatePrevious)
    }

    function errorHandler(error: ApiError): void {
      if (isConflictError(error)) {
        ;(error as ConflictApiError).conflictingElements.map(conflictingElement => {
          conflictingElement.queries = [normalizedQuery.value || query.value]
        })
        conflictApiError.value = error as ConflictApiError
        resolveConflictModal.value.open()
      } else {
        ctx.emit('showError', getErrorMessage(error))
      }
    }

    async function accept(): Promise<void> {
      await confirmAccept()
    }

    function cancel(): void {
      toolingForm.onCancel()
    }

    const { instanceConfig } = useInstance()

    async function loadSearchData(): Promise<void> {
      const { criteriaIsValid, userQuery } = curationToolCriteria
      if (criteriaIsValid.value) {
        searchData.value.loading = true
        try {
          const filters = FiltersUtils.getClientFilters(
            toolingForm.itemPivots.value,
            instanceConfig.value,
          )

          const params: ResultServiceGetParams = {
            query: userQuery.value ?? query.value,
            filters,
            page: {
              start: 0,
              rows: 12,
            },
          }

          const response = await resultService.get(params)

          const results = response.results.map(r => ({
            ...r,
            imageUrl: r.image,
          })) as unknown as Array<Result>

          searchData.value = {
            results,
            total: response.numFound,
            loading: false,
          }
        } catch (error) {
          ctx.emit('showError', getErrorMessage(error as ApiError))
          searchData.value = {
            results: [],
            total: 0,
            loading: false,
          }
        }
      }
    }

    async function confirmCriteriaChange(preserveRules = false): Promise<void> {
      try {
        ctx.emit('updateLoading', { visible: true })
        await setCriteria(changedCriteria.value, preserveRules)
      } catch (error) {
        ctx.emit('showError', getErrorMessage(error as ApiError))
      } finally {
        ctx.emit('updateLoading', { visible: false })
      }
      await loadSearchData()
    }

    async function changeCriteria(
      criteria: NextQueries['criteria'],
      preserveRules = false,
    ): Promise<void> {
      changedCriteria.value = criteria
      if (!toolingForm.item.value.data.rules.length || preserveRules) {
        await confirmCriteriaChange(preserveRules)
      } else {
        confirmCriteriaChangeModal.value.open()
      }
    }

    function onEnabledChange(value: boolean): Promise<void> {
      return changeCriteria(
        {
          ...toolingForm.item.value.criteria,
          enabled: value,
        },
        true,
      )
    }

    function onPivotChange(
      filter: RegularFilter,
      selectedValues: RegularFilterSelectedValues,
    ): Promise<void> {
      const pivots = updatePivots(toolingForm.item.value.criteria.pivots, filter, selectedValues)
      return changeCriteria({
        ...toolingForm.item.value.criteria,
        pivots,
      })
    }

    function onQueryChange(value: string): Promise<void> {
      return changeCriteria({
        ...toolingForm.item.value.criteria,
        queries: value ? [value] : [],
      })
    }

    function cancelCriteriaChange(): void {
      searchbox.value.resetInput()
    }

    async function resolveConflict(resolveConflictOption: ResolveConflictOption): Promise<void> {
      await toolingForm.resolveConflict({
        resolveConflictOption,
        errorHandler,
      })
    }

    toolingForm
      .initItem({ forceFetch: true })
      .then(async () => {
        if (!updateMode) {
          await changeCriteria(toolingForm.item.value.criteria, true)

          toolingForm.item.value.data.rules.forEach(rule => {
            rule.id = undefined
          })
        }

        await loadSearchData()
      })
      .finally(async () => {
        const isCreationMode = !updateMode
        if (isCreationMode && !toolingForm.isCloneMode()) {
          await updateItemWithContext(toolingForm.item)
          const queryValue = toolingForm.item.value.criteria.queries.at(0)

          if (queryValue) {
            await onQueryChange(queryValue)
          }
        }
      })

    return {
      ...toolingForm,
      ...curationToolCriteria,
      accept,
      baseClass,
      cancel,
      cancelCriteriaChange,
      confirmCriteriaChange,
      confirmCriteriaChangeModal,
      conflictApiError,
      isValid,
      query,
      messages,
      nextQueriesUpdateRoute: ROUTER_CONSTS.routes[FeatureIdEnum.NEXT_QUERIES].update,
      normalizedQuery,
      onEnabledChange,
      onPivotChange,
      onQueryChange,
      pivotsAreValid,
      PositionEnum,
      resolveConflict,
      resolveConflictModal,
      resultsPerRow,
      searchbox,
      searchData,
      updateMode,
    }
  },
})
